import type { SignupResumeView } from '@galapagos/shared';
import { stepAfterAccount } from '../health-plan/plan-details';
import { earliestMissingAnswerStep, type SignupEarlierAnswers } from '../resume/earlier-answers';
import type { SignupPath, SignupStepId } from '../steps';
import { readWithFallback } from './read-diagnostics';
import { SignupRequestError, type SignupApi } from './signup-api/signup-api';

/** How long the welcome-back page waits for the resume read before going on without it. */
export const SIGNUP_RESUME_WAIT_MS = 3000;

export const NOTHING_TO_RESUME: SignupResumeView = {
  firstName: null,
  lastName: null,
  clientRef: null,
};

/** `pending`, or a timeout rejection once `waitMs` has passed without an answer. */
export function within<T>(pending: Promise<T>, waitMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () =>
        reject(
          new SignupRequestError('The read did not answer in time', {
            kind: 'network',
            code: 'timeout',
          }),
        ),
      waitMs,
    );
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * The member's own resume details, or nothing when the read fails or has not
 * answered within `waitMs`: the page then greets them plainly and both buttons
 * go on without a restored name or employer. Never throws.
 */
export async function readSignupResumeWithin(
  api: SignupApi,
  waitMs: number = SIGNUP_RESUME_WAIT_MS,
): Promise<SignupResumeView> {
  const read = await readWithFallback('signup_resume_read', 'plain_welcome', () =>
    within(api.getSignupResume(), waitMs),
  );
  return read ?? NOTHING_TO_RESUME;
}

// The sponsor and sign-in params a resumed flow replaces; everything else on
// the link (language, campaign tags, a local fake scenario) rides along.
const REPLACED_PARAMS = ['client_id', 'sub_client_id', 'login', 'sso'] as const;

/**
 * The query string for a resumed flow: the current one with `client_id` set to
 * `clientId`, or none. The link's own `sub_client_id` stays only when
 * `keepSubClient` says the employer came from that same link.
 */
export function resumeQuery(
  current: string,
  clientId: string | undefined,
  keepSubClient = false,
): string {
  const params = new URLSearchParams(current);
  const subClientId = params.get('sub_client_id');
  for (const name of REPLACED_PARAMS) params.delete(name);
  if (clientId !== undefined) params.set('client_id', clientId);
  if (keepSubClient && clientId !== undefined && subClientId !== null) {
    params.set('sub_client_id', subClientId);
  }
  return params.toString();
}

/**
 * Where Continue lands: the first answer the path still needs before the
 * account (the focus pick on an employer link; the legal name or birth date on
 * the no-employer path), since every earlier answer is gone but the name.
 */
export function welcomeBackContinueStep(
  path: SignupPath,
  answers: SignupEarlierAnswers,
): SignupStepId {
  const afterAccount = stepAfterAccount({ features: undefined, flow: undefined, path });
  return earliestMissingAnswerStep(afterAccount, path, answers) ?? afterAccount;
}
